import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Share2, Sparkles } from 'lucide-react';
import { messagesApi } from '../services/messagesApi';
import { postsApi, renderPulseRecapImage } from '../services/postsApi';
import { storiesApi } from '../services/storiesApi';

// Weekly recap PREVIEW & approve. When the user has opted in, the first app
// open of each ISO week renders the Community Pulse recap and shows a preview
// dialog. Nothing is posted to their story until they explicitly approve —
// giving members full control over what gets shared on their behalf.
const RecapAutoPost = ({ language = 'en', user }) => {
  const es = language === 'es';
  const ran = useRef(false);
  const blobRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [skipping, setSkipping] = useState(false);

  useEffect(() => {
    if (!user || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const status = await messagesApi.recapStatus();
        if (!status?.due) return;
        const pulse = await messagesApi.pulse();
        const hasContent = (pulse?.new_testimonies || 0) > 0 || (pulse?.answered_prayers || 0) > 0 || (pulse?.top_streaks || []).length > 0;
        if (!hasContent) return;
        const blob = await renderPulseRecapImage(pulse, es);
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
        setOpen(true);
      } catch { /* silent — preview is best-effort */ }
    })();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, es]);

  const approve = async () => {
    if (!blobRef.current) return;
    setPosting(true);
    try {
      // Upload BEFORE claiming so a failed upload doesn't burn the week.
      const up = await postsApi.uploadImage(blobRef.current, 'weekly-recap.png');
      const claim = await messagesApi.recapMarkPosted();
      if (!claim?.claimed) { setOpen(false); return; }
      await storiesApi.create({
        kind: 'verse_image',
        image_url: up.image_url,
        caption: es ? 'Resumen semanal de la comunidad 🙌' : 'This week in Community 🙌',
        visibility: 'public',
      });
      window.dispatchEvent(new Event('lumera:stories-changed'));
      toast.success(es ? 'Compartimos el resumen semanal en tu historia 🙌' : "Shared this week's Pulse to your story 🙌");
      setOpen(false);
    } catch {
      toast.error(es ? 'No se pudo publicar el resumen' : "Couldn't post your recap");
    } finally {
      setPosting(false);
    }
  };

  // Skip this week: claim the week so we don't nag again until next week.
  const skipWeek = async () => {
    setSkipping(true);
    try { await messagesApi.recapMarkPosted(); } catch { /* ignore */ }
    setSkipping(false);
    setOpen(false);
    toast.info(es ? 'No compartiremos el resumen esta semana' : "We won't share your recap this week");
  };

  if (!previewUrl) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
      <DialogContent className="max-w-sm" data-testid="recap-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            {es ? 'Tu resumen semanal' : 'Your weekly recap'}
          </DialogTitle>
          <DialogDescription>
            {es
              ? 'Vista previa antes de compartir en tu historia. Nada se publica sin tu aprobación.'
              : 'Preview before it goes to your story. Nothing is posted without your approval.'}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl overflow-hidden border border-border">
          <img src={previewUrl} alt={es ? 'Resumen semanal' : 'Weekly recap'} className="w-full block" data-testid="recap-preview-image" />
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <Button onClick={approve} disabled={posting || skipping} className="w-full bg-gradient-to-r from-purple-600 to-rose-500 text-white" data-testid="recap-preview-approve">
            {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
            {es ? 'Publicar en mi historia' : 'Post to my story'}
          </Button>
          <Button onClick={skipWeek} disabled={posting || skipping} variant="ghost" className="w-full" data-testid="recap-preview-skip">
            {skipping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {es ? 'Esta semana no' : 'Not this week'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecapAutoPost;
